import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import TbLayoutNavbarCollapseFilled from "./TbLayoutNavbarCollapseFilled";
import TbLayoutNavbarFilled from "./TbLayoutNavbarFilled";
import TbLayoutNavbarInactive from "./TbLayoutNavbarInactive";
import { VisibilityModeCtx } from "../contexts/visibilityModeCtx";
import { VisibilityMode } from "../types";

const STORAGE_KEY = "navbar-visibility-mode";

export function getStoredVisibilityMode(): VisibilityMode {
    const stored = localStorage.getItem(STORAGE_KEY) as VisibilityMode | null;
    return Object.values(VisibilityMode).includes(stored as VisibilityMode)
        ? (stored as VisibilityMode)
        : VisibilityMode.Normal;
}

export function persistVisibilityMode(mode: VisibilityMode) {
    localStorage.setItem(STORAGE_KEY, mode);
}

export function useNavbarDisplayMode() {
    const { setVisibilityMode } = useContext(VisibilityModeCtx);
    const visibilityModes = Object.values(VisibilityMode) as VisibilityMode[];
    const { t } = useTranslation();

    const initialMode = getStoredVisibilityMode();
    const initialIndex = visibilityModes.indexOf(initialMode);
    const [index, setIndex] = useState(initialIndex);

    useEffect(() => {
        const mode = visibilityModes[index];
        setVisibilityMode(mode);
        persistVisibilityMode(mode);
    }, [index]);

    const iconMap = {
        [VisibilityMode.Normal]: TbLayoutNavbarInactive,
        [VisibilityMode.AutoHide]: TbLayoutNavbarCollapseFilled,
        [VisibilityMode.Fixed]: TbLayoutNavbarFilled,
    };

    const modes = visibilityModes.map((mode) => ({
        icon: iconMap[mode],
        label: t(`navBarItems.toolsMenu.navBar.mode.${mode}`),
    }));

    const toggle = () => {
        const nextIndex = (index + 1) % visibilityModes.length;
        setIndex(nextIndex);
    };

    const { icon: Icon, label } = modes[index];

    return {
        Icon,
        label,
        toggle
    };
}